const PT_TO_PX = 4 / 3;

/**
 * Extracts a `width` or `height` length from an inline CSS `style` string and
 * returns it as an integer number of pixels. Only `px` and `pt` units are
 * supported; any other unit (`%`, `em`, `auto`, …) or a missing declaration
 * returns `undefined`. Used to recover image dimensions before DOMPurify strips
 * the `style` attribute.
 */
export const parseStyleLengthAsPx = (
    style: string,
    property: 'width' | 'height',
) => {
    const declarationPattern = new RegExp(
        `(?:^|;)\\s*${property}\\s*:\\s*(\\d+(?:\\.\\d+)?)\\s*(px|pt)\\b`,
        'i',
    );
    const match = declarationPattern.exec(style);
    if (!match) return undefined;
    const value = parseFloat(match[1] ?? '');
    const unit = match[2]?.toLowerCase();
    const px = unit === 'pt' ? value * PT_TO_PX : value;
    return Math.round(px) || undefined;
};

/**
 * DOMPurify `uponSanitizeElement` hook. Many email clients (e.g. Outlook) put
 * image dimensions in the `style` attribute rather than the width/height
 * attributes. Since we strip `style` for security, we'd otherwise lose this
 * information and fall back to the attachment's intrinsic dimensions — which can
 * be much larger than intended (e.g. a 24pt social icon rendered at the 200px
 * clamp). This hook copies width/height from `style` onto the element's
 * attributes before `style` is stripped. Existing width/height attributes win,
 * since they were explicitly authored by the sender.
 */
export const liftImgStyleDimensions = (
    node: Node,
    data: { tagName?: string },
) => {
    if (data?.tagName !== 'img') return;
    const element = node as Partial<HTMLElement>;
    const style = element.getAttribute?.('style');
    if (!style) return;
    const styleWidth = parseStyleLengthAsPx(style, 'width');
    if (styleWidth !== undefined && !element.hasAttribute?.('width')) {
        element.setAttribute?.('width', String(styleWidth));
    }
    const styleHeight = parseStyleLengthAsPx(style, 'height');
    if (styleHeight !== undefined && !element.hasAttribute?.('height')) {
        element.setAttribute?.('height', String(styleHeight));
    }
};
